import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { colors as C } from "@/lib/theme";
import { Screen } from "@/components/Orig";
import { AdminHeader } from "@/components/AdminParts";
import ConsoleUsers from "@/pages/admin/ConsoleUsers";
import { ManualTab, ResourceTab } from "@/pages/admin/ConsoleContent";
import { KbTab, InsightsTab } from "@/pages/admin/ConsoleKb";
import { QuizTab, TrainingTab } from "@/pages/admin/ConsoleQuizTraining";

// Port of app/admin/console.tsx
const TABS = [["franchisee", "FRANCHISEES", "Franchisees"], ["manual", "OPS MANUAL", "Ops Manual"], ["resource", "RESOURCE", "Resource"], ["kb", "AI KB", "AI Knowledge Base"],
  ["insights", "AI INSIGHTS", "AI Insights"], ["lsoquiz", "LSO QUIZ", "LSO Quiz"], ["training", "CLEANING SETTINGS", "Cleaning Settings"]];

const useList = (path, initial = []) => {
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setBusy(true); setError("");
    try { setData(await api(path)); } catch (e) { setError(e.message || "Couldn't reach the server"); } finally { setBusy(false); }
  }, [path]);
  return { data, setData, busy, setBusy, error, load };
};

export default function AdminConsole() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "franchisee";
  const setTab = (k) => setParams((p) => { p.set("tab", k); p.delete("role"); return p; }, { replace: true });
  const users = useList("/admin/franchisees");
  const kb = useList("/kb");
  const quiz = useList("/admin/lso-quiz");
  const training = useList("/admin/training/settings");
  const master = useList("/manual/master", null);
  const [insights, setInsights] = useState({ stats: null, feedback: [], busy: false });

  const loadInsights = useCallback(async () => {
    setInsights((s) => ({ ...s, busy: true }));
    try {
      const [stats, feedback] = await Promise.all([api("/admin/ai/stats"), api("/admin/ai/feedback?limit=100")]);
      setInsights({ stats, feedback, busy: false });
    } catch { setInsights((s) => ({ ...s, busy: false })); }
  }, []);

  useEffect(() => { users.load(); master.load(); kb.load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "insights") loadInsights();
    if (tab === "lsoquiz") quiz.load();
    if (tab === "training") training.load();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Screen testId="admin-console-page">
      <div className="mx-auto max-w-[1100px]">
        <AdminHeader eyebrow="ADMIN · CONSOLE" title={TABS.find(([k]) => k === tab)?.[2] || "Corporate Console"} icon="grid-outline"
          right={<div className="flex gap-2"><button data-testid="console-invoicing-link" onClick={() => navigate("/admin/billing")} className="rounded-lg px-2.5 py-1.5 text-[11px] font-black tracking-[0.6px]" style={{ background: "rgba(76,204,6,0.12)", color: C.accent }}>Invoicing & Royalties</button><button data-testid="hub-link" onClick={() => navigate("/admin")} className="rounded-lg px-2.5 py-1.5 text-[11px] font-black tracking-[0.6px]" style={{ background: "#222", color: C.accent }}>← Admin Hub</button></div>} />
        <div className="flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none]" data-testid="console-tabs">
          {TABS.map(([k, label]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className="shrink-0 rounded-lg border px-3.5 py-2.5 text-xs font-bold tracking-[2px] transition-colors"
              style={{ borderColor: tab === k ? C.accent : C.border, background: tab === k ? "rgba(76,204,6,0.08)" : C.card, color: tab === k ? C.accent : C.textSecondary }}>{label}</button>
          ))}
        </div>
        <div className="p-6 pb-8 pt-4">
          {tab === "franchisee" && <ConsoleUsers list={users.data} setList={users.setData} loading={users.busy} error={users.error} reload={users.load} initialRole={params.get("role")} />}
          {tab === "manual" && <ManualTab master={master.data} reload={master.load} />}
          {tab === "resource" && <ResourceTab />}
          {tab === "kb" && <KbTab list={kb.data} reload={kb.load} />}
          {tab === "insights" && <InsightsTab {...insights} reload={loadInsights} />}
          {tab === "lsoquiz" && <QuizTab list={quiz.data} busy={quiz.busy} setBusy={quiz.setBusy} reload={quiz.load} />}
          {tab === "training" && <TrainingTab list={training.data} busy={training.busy} setBusy={training.setBusy} reload={training.load} />}
        </div>
      </div>
    </Screen>
  );
}
